import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ClipboardList, Camera, MapPin, Send, FileDown, X, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { onErr, downloadFile, compressImage, getPosition, num, todayStr } from "@/lib/providers";

export default function ProviderWork() {
  const qc = useQueryClient();
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ["spMe"], queryFn: async () => (await api.get("/sp/me")).data });
  const { data: logs } = useQuery({ queryKey: ["spLogs"], queryFn: async () => (await api.get("/sp/field-logs")).data });
  const active = (data?.work_orders || []).filter((w) => w.status === "Active");

  const [woId, setWoId] = useState("");
  const [f, setF] = useState({ log_date: todayStr(), area: "", units: "", extra: {}, notes: "" });
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const camRef = useRef(null);
  useEffect(() => { if (!woId && active.length) setWoId(active[0].id); }, [active, woId]);
  const wo = active.find((w) => w.id === woId);

  const addPhoto = async (file) => {
    if (photos.length >= 3) { toast.error("Up to 3 photos per log"); return; }
    setPhotos((p) => [...p, null]);
    const dataUrl = await compressImage(file);
    setPhotos((p) => { const n = [...p]; n[n.indexOf(null)] = dataUrl; return n; });
  };

  const submit = useMutation({
    mutationFn: async () => {
      setBusy(true);
      const pos = await getPosition();
      return (await api.post("/sp/field-logs", {
        work_order_id: woId, log_date: f.log_date, area: f.area, units_completed: Number(f.units),
        extra: Object.fromEntries(Object.entries(f.extra).map(([k, v]) => [k, Number(v || 0)])),
        notes: f.notes, ...pos, photos: photos.filter(Boolean),
      }, { timeout: 60000 })).data;
    },
    onSuccess: (d) => {
      if (d.outside_city) toast.warning(`Saved — but your location was ${d.distance_from_centre_km} km from the project city. IntenCiv will check it before approving.`);
      else toast.success("Work log submitted for verification");
      setF({ log_date: todayStr(), area: "", units: "", extra: {}, notes: "" }); setPhotos([]);
      qc.invalidateQueries({ queryKey: ["spLogs"] }); qc.invalidateQueries({ queryKey: ["spMe"] });
    },
    onError: onErr,
    onSettled: () => setBusy(false),
  });

  if (isLoading) return <Loading />;
  if (isError) return <ErrorState onRetry={refetch} />;
  const ready = woId && f.area.trim().length >= 2 && f.units !== "" && photos.filter(Boolean).length > 0 && !photos.includes(null);

  return (
    <div>
      <PageHeader overline="Service Provider" title="My Work" icon={ClipboardList}
        subtitle="Log the work you complete. You choose your own days and hours — IntenCiv pays for verified output." />

      {(data?.work_orders || []).length === 0 ? (
        <EmptyState icon={ClipboardList} title="No work orders yet" hint="IntenCiv will issue a work order before you start." />
      ) : (
        <div className="grid md:grid-cols-2 gap-3 mb-6">
          {data.work_orders.map((w) => (
            <Card key={w.id} className="p-4 border-slate-200 shadow-none">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-mono text-xs text-teal">{w.wo_number}</p>
                  <p className="font-semibold text-navy">{w.project_name}, {w.project_city}</p>
                  {w.assigned_area && <p className="text-xs text-slate-500">{w.assigned_area}</p>}
                </div>
                <StatusBadge status={w.status} />
              </div>
              <div className="h-2 rounded bg-slate-100 overflow-hidden mt-3"><div className="h-full bg-teal" style={{ width: `${Math.min(100, w.progress.percent)}%` }} /></div>
              <p className="text-xs text-slate-600 mt-1">{num(w.progress.verified_units)} of {num(w.target_units)} {w.unit_label.toLowerCase()} verified ({w.progress.percent}%)
                {w.progress.pending_units ? ` · ${num(w.progress.pending_units)} awaiting check` : ""}</p>
              <p className="text-xs text-slate-400 mt-1">{w.start_date} to {w.end_date}</p>
              <Button size="sm" variant="ghost" className="mt-1 -ml-2" onClick={() => downloadFile(`/sp/work-orders/${w.id}/pdf`, `WorkOrder_${w.wo_number}.pdf`)}><FileDown className="h-4 w-4 mr-1" /> Work order</Button>
            </Card>
          ))}
        </div>
      )}

      {active.length > 0 && (
        <Card className="p-4 sm:p-5 border-slate-200 shadow-none mb-6">
          <p className="font-semibold text-navy mb-3">Add work log</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {active.length > 1 && (
              <div className="sm:col-span-2">
                <Label>Work order</Label>
                <Select value={woId} onValueChange={setWoId}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{active.map((w) => <SelectItem key={w.id} value={w.id}>{w.wo_number} · {w.project_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div><Label>Date of work</Label><Input type="date" className="mt-1" value={f.log_date} max={todayStr()} onChange={(e) => setF({ ...f, log_date: e.target.value })} /></div>
            <div><Label>Area / ward / locality *</Label><Input className="mt-1" value={f.area} onChange={(e) => setF({ ...f, area: e.target.value })} placeholder="Ward 12 – Hasanbagh" /></div>
            <div><Label>{wo?.unit_label || "Units completed"} *</Label><Input type="number" inputMode="numeric" min={0} className="mt-1" value={f.units} onChange={(e) => setF({ ...f, units: e.target.value })} /></div>
            {(wo?.log_fields || []).map((lf) => (
              <div key={lf.key}><Label>{lf.label}</Label><Input type="number" inputMode="numeric" min={0} className="mt-1" value={f.extra[lf.key] ?? ""}
                onChange={(e) => setF({ ...f, extra: { ...f.extra, [lf.key]: e.target.value } })} /></div>
            ))}
            <div className="sm:col-span-2"><Label>Notes (optional)</Label><Textarea rows={2} className="mt-1" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
          </div>

          <div className="mt-3">
            <Label>Photos from the field * (1–3)</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {photos.map((p, i) => (
                <div key={i} className="relative h-20 w-20 rounded-md border border-slate-200 overflow-hidden bg-slate-50 grid place-items-center">
                  {p ? <img src={p} alt="" className="h-full w-full object-cover" /> : <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
                  {p && <button className="absolute top-0.5 right-0.5 bg-white/90 rounded-full p-0.5" onClick={() => setPhotos(photos.filter((_, j) => j !== i))}><X className="h-3 w-3" /></button>}
                </div>
              ))}
              {photos.length < 3 && (
                <button className="h-20 w-20 rounded-md border border-dashed border-slate-300 grid place-items-center text-slate-500 hover:bg-slate-50" onClick={() => camRef.current?.click()}>
                  <Camera className="h-5 w-5" />
                </button>
              )}
              <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden"
                onChange={(e) => { const file = e.target.files?.[0]; e.target.value = ""; if (file) addPhoto(file); }} />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 mt-4">
            <Button className="bg-navy hover:bg-navy-700" disabled={!ready || busy} onClick={() => submit.mutate()}>
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />} Submit log
            </Button>
            <p className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> Your location is attached when you submit, to confirm the work was in {wo?.project_city}.</p>
          </div>
        </Card>
      )}

      <p className="font-semibold text-navy mb-2">My recent logs</p>
      {!logs?.length ? <p className="text-sm text-slate-400">No logs yet.</p> : (
        <div className="space-y-2">
          {logs.slice(0, 30).map((l) => (
            <Card key={l.id} className="p-3 border-slate-200 shadow-none flex flex-wrap items-center gap-x-4 gap-y-1">
              <span className="text-sm text-slate-500 w-24">{l.log_date}</span>
              <span className="text-sm font-medium text-navy flex-1 min-w-[140px]">{l.area}</span>
              <span className="text-sm">{num(l.units_completed)}{l.verified_units != null && l.verified_units !== l.units_completed && <span className="text-amber-700"> → {num(l.verified_units)}</span>}</span>
              {l.outside_city && <span className="text-xs text-red-600 inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> outside city</span>}
              <StatusBadge status={l.status} />
              {l.decision_remark && <span className="text-xs text-slate-500 w-full">{l.decision_remark}</span>}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
